import { Box, Flex, HStack, Text } from "@chakra-ui/react";
import useTranslate from "@raiden/library/hooks/useTranslate";
import { memo } from "react";
import { Href } from "../../../../../components/Href";
import { Icon } from "../../../../../components/Icon";
import { CONTENT_WIDTH } from "../../../../../constants/Layout";
import { Z_INDEX } from "../../../../../constants/zIndex";
import {
  getMenuItemAdapterType,
  getMenuItemAdapterValues,
} from "../../../../../helpers/menus/getMenuItemAdapterValues";
import { useDelayedSetState } from "../../../../../hooks/useDelayedCall";

export const MainMenuItem = memo(
  /**
   * @typedef {Object} Props
   * @property {import("@raiden/library/types/Configuration").ConfigurationMenuItem} menuItem
   * @property {number} index
   * @property {(panelId: number | null) => void} setActivePanelId
   * @property {boolean} isActive
   * @property {boolean} isAnyPanelActive
   * @property {boolean} isLast
   */
  /**
   * @param {Props} props
   */
  function MainMenuItem({
    menuItem,
    index,
    isActive,
    isAnyPanelActive,
    setActivePanelId,
    isLast,
  }) {
    const translate = useTranslate();

    const type = getMenuItemAdapterType({ menuItem });

    const { delayedCall, cancelDelayedCall } = useDelayedSetState();

    if (type === undefined) {
      return null;
    }

    const {
      title,
      href: _href,
      target,
    } = getMenuItemAdapterValues({
      menuItem,
      translate,
    });

    const href = type === "page_category" ? undefined : _href;

    return (
      <Href
        href={href}
        target={href ? target : undefined}
        display="flex"
        w="full">
        <Flex
          onMouseEnter={() => {
            if (isActive || menuItem.children.length === 0) {
              setActivePanelId(index);
            } else {
              delayedCall(() => {
                setActivePanelId(index);
              }, 300);
            }
          }}
          onMouseLeave={() => {
            cancelDelayedCall();
            setActivePanelId(null);
          }}
          onFocus={() => {
            setActivePanelId(index);
          }}
          role="group"
          zIndex={Z_INDEX.HEADER_PANEL}
          alignItems="stretch"
          tabIndex={0}
          w="full">
          <Flex
            flexGrow={1}
            flexBasis={0}
            backgroundColor={isActive ? "#fff" : undefined}>
            <Box w="full" pt="2.125rem">
              <HStack
                justify="center"
                spacing="0"
                w="full"
                borderLeftWidth="1px"
                borderRightWidth={isLast ? "1px" : "0"}
                borderColor={isActive ? "#fff" : "brandPrimary.600"}
                sx={{
                  [`@media (max-width: ${CONTENT_WIDTH})`]: {
                    borderRightWidth: "0",
                  },
                }}>
                <Text
                  fontSize="1rem"
                  fontWeight={400}
                  lineHeight="1.375rem"
                  whiteSpace="nowrap"
                  position="relative"
                  color={isActive ? "brandPrimary.700" : "#fff"}
                  py=".5rem"
                  textAlign="center">
                  {title}
                </Text>

                {menuItem.children.length > 0 && (
                  <Icon
                    icon="ms_arrow_drop_down"
                    color={isActive ? "brandPrimary.700" : "#fff"}
                  />
                )}
              </HStack>
            </Box>
          </Flex>
        </Flex>
      </Href>
    );
  },
);
